import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useLocalization } from "../../hooks/useLocalization";

interface ImageLightboxProps {
  open: boolean;
  src: string | null;
  alt: string;
  title: string;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  hasPrev?: boolean;
  hasNext?: boolean;
}

export const ImageLightbox = ({
  open,
  src,
  alt,
  title,
  onClose,
  onPrev,
  onNext,
  hasPrev = false,
  hasNext = false,
}: ImageLightboxProps) => {
  const { strings } = useLocalization();
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    const scrollY = window.scrollY;
    const previousOverflow = document.body.style.overflow;
    const previousPosition = document.body.style.position;
    const previousTop = document.body.style.top;
    const previousWidth = document.body.style.width;

    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${String(scrollY)}px`;
    document.body.style.width = "100%";
    closeButtonRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
      if (event.key === "ArrowLeft" && hasPrev) {
        onPrev?.();
      }
      if (event.key === "ArrowRight" && hasNext) {
        onNext?.();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.position = previousPosition;
      document.body.style.top = previousTop;
      document.body.style.width = previousWidth;
      window.scrollTo(0, scrollY);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose, onPrev, onNext, hasPrev, hasNext]);

  if (!open || !src) {
    return null;
  }

  const navButtonClass =
    "absolute top-1/2 z-[2] inline-flex min-h-10 min-w-10 -translate-y-1/2 items-center justify-center rounded-full border border-[var(--surface-card-border)] bg-[rgba(16,16,29,0.85)] text-white max-md:min-h-11 max-md:min-w-11";

  return createPortal(
    <div
      className="fixed inset-0 z-[100001] flex h-svh flex-col bg-[rgba(8,8,16,0.96)] p-[max(0.5rem,env(safe-area-inset-top))_max(0.5rem,env(safe-area-inset-right))_max(0.5rem,env(safe-area-inset-bottom))_max(0.5rem,env(safe-area-inset-left))]"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
    >
      <div className="flex shrink-0 justify-end">
        <button
          ref={closeButtonRef}
          type="button"
          className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-[0.5rem] border border-[var(--surface-card-border)] bg-[var(--surface-card-bg)] text-white transition-opacity hover:opacity-90"
          onClick={(event) => {
            event.stopPropagation();
            onClose();
          }}
          aria-label={strings.TCG.CLOSE}
        >
          <X size={22} aria-hidden="true" />
        </button>
      </div>

      <div
        className="relative flex min-h-0 flex-1 items-stretch justify-center"
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        {hasPrev ? (
          <button
            type="button"
            className={`${navButtonClass} left-1 max-md:left-2`}
            onClick={onPrev}
            aria-label={strings.TCG.PREV}
          >
            <ChevronLeft size={24} aria-hidden="true" />
          </button>
        ) : null}

        <figure className="flex min-h-0 w-full max-w-[min(100%,48rem)] flex-1 flex-col items-center gap-2 px-2 max-md:max-w-none max-md:px-3">
          <div className="flex min-h-0 w-full flex-1 items-center justify-center">
            <img
              src={src}
              alt={alt}
              className="max-h-full max-w-full object-contain"
              decoding="async"
            />
          </div>
          <figcaption className="max-w-full shrink-0 px-1 pb-1 text-center font-shentox text-[0.85rem] font-semibold tracking-[0.04em] text-light-blue uppercase max-md:text-[0.8rem]">
            {title}
          </figcaption>
        </figure>

        {hasNext ? (
          <button
            type="button"
            className={`${navButtonClass} right-1 max-md:right-2`}
            onClick={onNext}
            aria-label={strings.TCG.NEXT}
          >
            <ChevronRight size={24} aria-hidden="true" />
          </button>
        ) : null}
      </div>
    </div>,
    document.body,
  );
};
